'use client';

import type React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { ButtonTw as Button } from './button-tw';
import { Link } from './link';

/**
 * Root container for a dropdown menu, based on Headless UI.
 */
export function DropdownTw(props: Headless.MenuProps) {
  return <Headless.Menu {...props} />;
}

type IBooleanString = 'true' | 'false';

/**
 * The button that toggles the dropdown menu.
 */
export function DropdownButtonTw<T extends React.ElementType = typeof Button>({
  as = Button,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string; 
  /** Whether the button should have a plain style. */
  plain?: IBooleanString 
} & Omit<
  Headless.MenuButtonProps<T>,
  'className'
>) {
  return <Headless.MenuButton as={as} {...props} />;
}

/**
 * The menu containing dropdown items.
 */
export function DropdownMenuTw({
  anchor = 'bottom',
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.MenuItemsProps, 'as' | 'className'>) {
  return (
    <Headless.MenuItems
      {...props}
      anchor={anchor}
      className={clsx(
        className,
        // Anchor positioning
        '[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)]',
        'data-[anchor~=end]:[--anchor-offset:6px]',
        'data-[anchor~=start]:[--anchor-offset:-6px]',
        'sm:data-[anchor~=end]:[--anchor-offset:4px]',
        'sm:data-[anchor~=start]:[--anchor-offset:-4px]',
        // Base styles
        'isolate w-max rounded-xl p-1',
        // Invisible border that is only visible in `forced-colors` mode for
        // accessibility purposes
        'focus:outline-hidden outline outline-transparent',
        // Handle scrolling when menu won't fit in viewport
        'overflow-y-auto',
        // Popover background
        'bg-white/75 backdrop-blur-xl dark:bg-gray-800/75',
        // Shadows
        'shadow-lg ring-1 ring-gray-950/10 dark:ring-inset dark:ring-white/10',
        // Define grid at the menu level if subgrid is supported
        'supports-[grid-template-columns:subgrid]:grid',
        'supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        // Transitions
        'data-leave:duration-100 data-leave:ease-in transition',
        'data-closed:data-leave:opacity-0'
      )}
      transition
    />
  );
}

/**
 * An individual item within the dropdown menu.
 */
export function DropdownItemTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & (
  | Omit<Headless.MenuItemProps<'button'>, 'as' | 'className'>
  | Omit<Headless.MenuItemProps<typeof Link>, 'as' | 'className'>
)) {
  const classes = clsx(
    className,
    // Base styles
    'group gap-2 cursor-default rounded-lg ps-3.5 pe-2 py-2.5 focus:outline-hidden',
    'sm:ps-3 sm:pe-1 sm:py-1.5 transition duration-500 ease-out',
    // Text styles
    'text-start text-base/6 text-gray-950 sm:text-sm/6 dark:text-white',
    'forced-colors:text-[CanvasText]',
    // Focus
    'data-focus:bg-primary-500/10 data-focus:text-primary-700 transition',
    'duration-1000 ease-in cursor-pointer',
    'dark:data-focus:text-primary-400 ',
    // Current active or selected item
    'data-current:bg-primary-500/10 data-current:text-primary-700 transition',
    'duration-1000 ease-in cursor-pointer',
    'dark:data-current:text-primary-400 ',
    // Disabled state
    'data-disabled:opacity-50',
    // Forced colors mode
    'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight]',
    'forced-colors:data-focus:text-[HighlightText]',
    'forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]',
    // Use subgrid when available but fallback to an explicit grid layout if not
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center',
    'supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
    // Icons
    '*:data-[slot=icon]:col-start-1 *:data-[slot=icon]:row-start-1',
    '*:data-[slot=icon]:mr-2.5 *:data-[slot=icon]:-ml-0.5 *:data-[slot=icon]:size-5',
    'sm:*:data-[slot=icon]:mr-2 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-gray-500 data-focus:*:data-[slot=icon]:text-primary',
    'dark:*:data-[slot=icon]:text-gray-400',
    'dark:data-focus:*:data-[slot=icon]:text-action-icon-focused',
    'dark:data-current:*:data-[slot=icon]:text-inherit',
    // Avatar
    '*:data-[slot=avatar]:mr-2.5 *:data-[slot=avatar]:-ml-1',
    '*:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:mr-2',
    'sm:*:data-[slot=avatar]:size-5'
  );

  return 'href' in props ? (
    <Headless.MenuItem as={Link} {...props} className={classes} />
  ) : (
    <Headless.MenuItem
      as="button"
      type="button"
      {...props}
      className={classes}
    />
  );
}

export function DropdownHeaderTw({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(className, 'col-span-5 px-3.5 pb-1 pt-2.5 sm:px-3')}
    />
  );
}

/**
 * A grouping section within the dropdown menu.
 */
export function DropdownSectionTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.MenuSectionProps,
  'as' | 'className'
>) {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        // Define grid at the section level instead of the item level if subgrid is supported
        'col-span-full supports-[grid-template-columns:subgrid]:grid',
        'supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]'
      )}
    />
  );
}

/**
 * The heading for a dropdown section.
 */
export function DropdownHeadingTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.MenuHeadingProps,
  'as' | 'className'
>) {
  return (
    <Headless.MenuHeading
      {...props}
      className={clsx(
        className,
        'col-span-full grid grid-cols-[1fr_auto] gap-x-12 px-3.5 pb-1 pt-2',
        'text-sm/5 font-medium text-gray-500 dark:text-gray-400 sm:px-3 sm:text-xs/5'
      )}
    />
  );
}

/**
 * A divider between dropdown items or sections.
 */
export function DropdownDividerTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.MenuSeparatorProps,
  'as' | 'className'
>) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'col-span-full mx-3.5 my-1 h-px border-0 bg-gray-950/5 sm:mx-3',
        'forced-colors:bg-[CanvasText] dark:bg-white/10'
      )}
    />
  );
}

/**
 * A label for a dropdown item.
 */
export function DropdownLabelTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.LabelProps, 'as' | 'className'>) {
  return (
    <Headless.Label
      {...props}
      className={clsx(className, 'col-start-2 row-start-1 flex')}
      data-slot="label"
      // {...props}
    />
  );
}

/**
 * A brief description for a dropdown item.
 */
export function DropdownDescriptionTw({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DescriptionProps,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'text-description-text col-span-2 col-start-2 row-start-2 text-sm/5',
        'group-data-focus:text-description-text dark:text-gray-400 sm:text-xs/5',
        'forced-colors:group-data-focus:text-[HighlightText]'
      )}
    />
  );
}

/**
 * A shortcut key hint for a dropdown item.
 */
export function DropdownShortcutTw({
  keys,
  className,
  ...props
}: { 
  /** The key(s) to display as a shortcut. */
  keys: string | string[]; 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DescriptionProps<'kbd'>,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(
        className,
        'col-start-5 row-start-1 flex justify-self-end'
      )}
    >
      {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
        <kbd
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={clsx([
            'min-w-[2ch] text-center font-sans capitalize text-gray-400 ',
            'group-data-focus:text-white forced-colors:group-data-focus:text-[HighlightText]',
            // Make sure key names that are longer than one character (like "Tab") have extra space
            index > 0 && char.length > 1 && 'pl-1',
          ])}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  );
}
